import { useState, useEffect, useMemo, useCallback } from "react";
import { Typography, Button, Box, Skeleton } from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAuth } from "../../../resources/AuthProvider";
import CadastroUsuarioForm from "../../molecules/CadastroUsuarioForm/CadastroUsuarioForm";
import { CADASTRO_USER_UPDATED_EVENT, useCadastro } from "../../../resources/CadastroProvider";
import PessoalForm from "../../molecules/PessoalForm/PessoalForm";
import ContatoForm from "../../molecules/ContatoForm/ContatoForm";
import EnderecoForm from "../../molecules/EnderecoForm/EnderecoForm";
import CadastroProfissionaisForm from "../../molecules/CadastroProfissionaisForm/CadastroProfissionaisForm";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { organizerUser } from "./organizerUser";
import {
  AddressDetail,
  ContactDetail,
  Disability,
  PersonalData,
  ProfessionalData,
  RoleType,
} from "../../../types/Models";
import { NovoUsuario } from "../../../types/User";

import styles from "./VisualizacaoDadosUsuario.module.css";
import styles_general from "../../../styles/General.module.css";
import styles_button from "../../../styles/Button.module.css";

export interface dadosCompletos {
  usuario: NovoUsuario;
  pessoais: PersonalData;
  endereco: AddressDetail;
  contato: ContactDetail;
  profissionais: ProfessionalData;
  disabilities?: Disability[];
}

const VisualizacaoDadosUsuarioSkeleton = () => (
  <Box
    sx={{ p: { xs: 2, md: 3 } }}
    aria-busy="true"
    aria-label="Carregando dados do usuário"
  >
    <Skeleton variant="text" width={360} height={54} />
    <Skeleton variant="text" width="58%" height={30} sx={{ mb: 3 }} />
    <Box sx={{ display: "grid", gap: 2 }}>
      {Array.from({ length: 5 }, (_, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            border: "1px solid #D4DCF8",
            borderRadius: "8px",
          }}
        >
          <Skeleton variant="rounded" width={60} height={60} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="34%" height={32} />
            <Skeleton variant="text" width="72%" height={24} />
          </Box>
          <Skeleton variant="circular" width={30} height={30} />
        </Box>
      ))}
    </Box>
  </Box>
);

const VisualizacaoDadosUsuario = () => {
  const {
    userData,
    setUserData,
    personalData,
    setPersonalData,
    addressData,
    setAddressData,
    contactData,
    setContactData,
    professionalData,
    setProfessionalData,
    //disabilities,
    setDisabilities,
    //areasActivity,
    setAreasActivity
  } = useCadastro();

  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  //const [dadosAnterior, setDadosAnterior] = useState<dadosCompletos>();

  const [searchParams] = useSearchParams();
  const cpf = searchParams.get("cpf");
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const isEditProfileRoute =
    location.pathname === "/editar-perfil" || (location.pathname === "/editar-perfil" && !cpf);
  const isAdminEditRoute = location.pathname === "/user" && !!cpf;
  const isLoggedUserData = isEditProfileRoute || !cpf;
  const isCommonLoggedUser =
    user?.role === RoleType.USUARIO || user?.active_role?.nome === RoleType.USUARIO;
  const canEditOwnProfessionalData = useMemo(() => {
    if (!isLoggedUserData) {
      return true;
    }

    const updatedAt = professionalData.updated_at;

    if (!updatedAt) {
      return true;
    }

    const updatedAtDate = new Date(updatedAt);

    if (Number.isNaN(updatedAtDate.getTime())) {
      return false;
    }

    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    return updatedAtDate <= sixMonthsAgo;
  }, [isLoggedUserData, professionalData.updated_at]);

  const loadUserData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    await organizerUser(
      cpf,
      user,
      setUserData,
      setPersonalData,
      setAddressData,
      setContactData,
      setProfessionalData,
      setLoading,
      setDisabilities,
      setAreasActivity,
      //setDadosAnterior
    );
  }, [
    cpf,
    setAddressData,
    setAreasActivity,
    setContactData,
    setDisabilities,
    setPersonalData,
    setProfessionalData,
    setUserData,
    user,
  ]);

  useEffect(() => {
    void loadUserData();
  }, [loadUserData, location.key]);

  useEffect(() => {
    const handleUserUpdated = () => {
      if (isEditProfileRoute) {
        void loadUserData();
      }
    };

    window.addEventListener(CADASTRO_USER_UPDATED_EVENT, handleUserUpdated);

    return () => {
      window.removeEventListener(CADASTRO_USER_UPDATED_EVENT, handleUserUpdated);
    };
  }, [isEditProfileRoute, loadUserData]);

  /*const handleEdit = (idx: number) => {
    setEditIndex(idx);
  };*/

  /*const handleClose = () => {
    setEditIndex(null);
  };*/

  /*
  const handleSubmitSection = async (section: ValidKey) => {
    if (!user) return;

    const sendCPF = !cpf ? user.cpf : cpf;

    const data = {
      usuario: userData,
      endereco: addressData,
      contato: contactData,
      pessoais: personalData,
      profissionais: professionalData,
    };

    const payload = {
      section,
      data: data[section],
    } as UpdatePayload;
    if (!dadosAnterior) {
      return;
    }
    try {
      const oldData = dadosAnterior[section] as any;
      const response = await submitUserData(sendCPF, payload);
      const newData = { ...data[section] } as any;

      // Iterar sobre as chaves do objeto atualizado para registrar alterações
      for (const key in newData) {
        if (newData[key] !== oldData[key]) {
          await registrarAlteracao(
            section, // Nome da tabela
            key, // Nome do campo alterado
            oldData[key] ? String(oldData[key]) : null, // Valor antigo
            newData[key] ? String(newData[key]) : null // Novo valor
          );
        }
      }
    } catch (error) {
      console.error(`Error submitting ${section} data`, error);
    }

    //handleClose();
  };
  */

  if (loading) {
    return <VisualizacaoDadosUsuarioSkeleton />;
  }

  const hasData =
    userData.nome ||
    personalData.data_nascimento ||
    addressData.cep ||
    contactData.telefone_celular ||
    professionalData.ambito;

  if (!hasData) {
    return (
      <div className={styles["dados-usuario"]}>
        <Typography>Nenhum dado disponível</Typography>
      </div>
    );
  }

  const sectionCards = [
    {
      id: "usuario",
      title: "Dados do Usuário",
      description: "Nome, CPF, E-mail e Alteração de Senha",
      icon: <PersonOutlineOutlinedIcon sx={{ color: "#4D73E8", fontSize: 34 }} />,
      form: (
        <CadastroUsuarioForm
          edit={true}
          isOwnProfileEdit={isEditProfileRoute}
          isCommonUser={isEditProfileRoute && isCommonLoggedUser}
          showUserTypeField={!isEditProfileRoute}
        />
      ),
    },
    {
      id: "pessoais",
      title: "Dados pessoais",
      description: "Data de nascimento, identidade de gênero, escolaridade, profissão, dentre outros",
      icon: <BadgeOutlinedIcon sx={{ color: "#4D73E8", fontSize: 34 }} />,
      form: <PessoalForm edit={true} />,
    },
    {
      id: "contato",
      title: "Dados de contato",
      description: "Telefone celular, fixo, institucional e e-mail secundário",
      icon: <CallOutlinedIcon sx={{ color: "#4D73E8", fontSize: 34 }} />,
      form: <ContatoForm edit={true} />,
    },
    {
      id: "endereco",
      title: "Dados de Endereço",
      description: "CEP, Cidade, Logradouro, dentre outros",
      icon: <LocationOnOutlinedIcon sx={{ color: "#4D73E8", fontSize: 34 }} />,
      form: <EnderecoForm edit={true} />,
    },
    {
      id: "profissionais",
      title: "Dados Profissionais",
      description: "Âmbito de atuação, Município de atuação, Órgão/Lotação, dentre outros",
      icon: <WorkOutlineOutlinedIcon sx={{ color: "#4D73E8", fontSize: 34 }} />,
      form: (
        <CadastroProfissionaisForm
          edit={true}
          isOwnProfileEdit={isEditProfileRoute}
          canEditOwnProfessionalData={isAdminEditRoute ? true : canEditOwnProfessionalData}
        />
      ),
    },
  ];

  const selectedCard = sectionCards.find((section) => section.id === selectedSection);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: { xs: "2rem", md: "2.35rem" },
          fontWeight: 700,
          letterSpacing: "-0.01em",
          lineHeight: 1.15,
          color: "#4D73E8",
          mb: 1,
          textAlign: "left",
        }}
      >
        {isAdminEditRoute ? "Alterar Informações do Usuário" : "Editar meu perfil/Alterar senha"}
      </Typography>
      <Typography
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: { xs: "0.95rem", md: "1rem" },
          fontWeight: 400,
          color: "#7A7A7A",
          mb: 3,
          textAlign: "left",
        }}
      >
        Aqui você pode acessar seus dados e editá-los por bloco do cadastro.
      </Typography>

      {selectedCard ? (
        <>
          <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
            <Button variant="contained" className={styles_button.buttonSecondary} onClick={() => setSelectedSection(null)}>
              Voltar para opções
            </Button>
          </Box>

          <div className={styles_general.card}>{selectedCard.form}</div>
        </>
      ) : (
        <Box sx={{ display: "grid", gap: 2 }}>
          {sectionCards.map((section) => (
            <Box
              key={section.id}
              onClick={() => setSelectedSection(section.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                border: "1px solid #D4DCF8",
                borderRadius: "8px",
                backgroundColor: "#FBFCFF",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#F3F6FF",
                  borderColor: "#B9C7F8",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "6px",
                    backgroundColor: "#E6ECFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {section.icon}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: { xs: "1.5rem", md: "1.95rem" },
                      fontWeight: 700,
                      letterSpacing: "-0.01em",
                      color: "#4D73E8",
                      lineHeight: 1.15,
                      textAlign: "left",
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: { xs: "0.9rem", md: "1rem" },
                      fontWeight: 400,
                      color: "#6F6F7A",
                      mt: 0.5,
                      textAlign: "left",
                    }}
                  >
                    {section.description}
                  </Typography>
                </Box>
              </Box>
              <ChevronRightIcon sx={{ color: "#8A8AA2", fontSize: 34, flexShrink: 0 }} />
            </Box>
          ))}
          {!isLoggedUserData && (
            <Box sx={{ mt: 1 }}>
              <Button variant="contained" className={styles_button.buttonSecondary} onClick={() => navigate(-1)}>
                Voltar
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default VisualizacaoDadosUsuario;

/*
<div className={styles["dados-usuario"]}>
  <UserDetails
    userData={userData}
    personalData={personalData}
    addressData={addressData}
    contactData={contactData}
    professionalData={professionalData}
    forms={forms}
    editIndex={editIndex}
    handleEdit={handleEdit}
    handleClose={handleClose}
    handleSubmitSection={handleSubmitSection}
  />
</div>
*/
